import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar,
  Clock,
  Search,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Activity,
  Copy,
  Check,
  ChevronRight,
  X,
  FileText,
  Filter,
  PlayCircle,
  ChevronLeft,
  Timer,
  Archive,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showSuccess, showError } from '../../../utils/toast';
import { OracleBackupView } from './OracleBackupView';

export interface OracleScheduledJob {
  agent_id?: number;
  asset_id?: number;
  client_id?: number;
  cluster_id?: string;
  collected_at?: string;
  config_id?: string;
  db_type?: string;
  enabled: boolean;
  hostname?: string;
  indexed_at?: string;
  ip?: string;
  last_run_at?: string;
  last_run_duration_sec?: number;
  last_run_status?: string;
  log_timestamp?: string;
  master_agent_id?: string;
  name: string;
  next_run_at?: string;
  organization_id?: number;
  os?: string;
  owner?: string;
  schedule?: string;
  schedule_type?: string;
}

interface OracleScheduledJobsViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const formatDate = (isoString?: string): string => {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return isoString;
  }
};

const formatRelativeTime = (isoString?: string): string => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const isFuture = diffMs > 0;
    const absMs = Math.abs(diffMs);
    const diffSec = Math.floor(absMs / 1000);
    if (diffSec < 60) return isFuture ? 'in a few seconds' : `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return isFuture ? `in ${diffMin}m` : `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return isFuture ? `in ${diffHours}h` : `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return isFuture ? `in ${diffDays}d` : `${diffDays}d ago`;
  } catch {
    return '';
  }
};

export const OracleScheduledJobsView: React.FC<OracleScheduledJobsViewProps> = ({
  activeCluster,
  clientId,
  refreshKey = 0,
  dbType,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'jobs' | 'backups'>('jobs');
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [jobs, setJobs] = useState<OracleScheduledJob[]>([]);
  const [capability, setCapability] = useState<{ mechanism?: string; status?: string } | null>(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ENABLED' | 'DISABLED'>('ALL');
  const [scheduleTypeFilter, setScheduleTypeFilter] = useState<string>('ALL');
  const [ownerFilter, setOwnerFilter] = useState<string>('ALL');
  const [lastRunStatusFilter, setLastRunStatusFilter] = useState<string>('ALL');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Drawer modal
  const [selectedJob, setSelectedJob] = useState<OracleScheduledJob | null>(null);
  const [copiedName, setCopiedName] = useState<string | null>(null);

  const fetchJobs = useCallback(
    async (isManual = false) => {
      if (!activeCluster) return;
      if (isManual) setRefreshing(true);
      else setLoading(true);

      try {
        const params: any = { cluster_id: activeCluster };
        if (clientId) params.client_id = clientId;

        const res = await baseapi.get('/db_monitor_dashboard/scheduled_jobs', { params });
        const rawPayload = res.data;
        const rawData = rawPayload?.data || (Array.isArray(rawPayload) ? rawPayload : []);

        setJobs(Array.isArray(rawData) ? rawData : []);
        setCapability(rawPayload?.capability || null);
      } catch (err: any) {
        console.error('Failed to fetch Oracle scheduled jobs:', err);
        showError(err.response?.data?.message || err.message || 'Failed to fetch scheduled jobs');
        setJobs([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeCluster, clientId]
  );

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, refreshKey]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedName(text);
    showSuccess('Copied to clipboard');
    setTimeout(() => setCopiedName(null), 1800);
  };

  // Unique lists for dropdowns
  const scheduleTypesList = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach((j) => {
      if (j.schedule_type) set.add(j.schedule_type);
    });
    return Array.from(set).sort();
  }, [jobs]);

  const ownersList = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach((j) => {
      if (j.owner) set.add(j.owner);
    });
    return Array.from(set).sort();
  }, [jobs]);

  // Counts
  const totalCount = jobs.length;
  const enabledCount = jobs.filter((j) => j.enabled === true).length;
  const disabledCount = jobs.filter((j) => j.enabled === false).length;
  const succeededCount = jobs.filter((j) => (j.last_run_status || '').toUpperCase() === 'SUCCEEDED').length;
  // const failedCount = jobs.filter((j) => {
  //   const st = (j.last_run_status || '').toUpperCase();
  //   return st === 'FAILED' || st === 'ERROR' || st === 'STOPPED';
  // }).length;
  const hasUpcomingNextRun = jobs.filter((j) => !!j.next_run_at).length;

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      // Search
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const inName = job.name?.toLowerCase().includes(q);
        const inOwner = job.owner?.toLowerCase().includes(q);
        const inSchedule = job.schedule?.toLowerCase().includes(q);
        const inType = job.schedule_type?.toLowerCase().includes(q);
        const inLastStatus = job.last_run_status?.toLowerCase().includes(q);
        if (!inName && !inOwner && !inSchedule && !inType && !inLastStatus) return false;
      }

      // Status
      if (statusFilter === 'ENABLED' && !job.enabled) return false;
      if (statusFilter === 'DISABLED' && job.enabled) return false;

      // Schedule Type
      if (scheduleTypeFilter !== 'ALL' && job.schedule_type !== scheduleTypeFilter) return false;

      // Owner
      if (ownerFilter !== 'ALL' && job.owner !== ownerFilter) return false;

      // Last Run Status
      if (lastRunStatusFilter !== 'ALL') {
        const st = (job.last_run_status || 'NEVER').toUpperCase();
        if (lastRunStatusFilter === 'SUCCEEDED' && st !== 'SUCCEEDED') return false;
        if (lastRunStatusFilter === 'FAILED' && st !== 'FAILED' && st !== 'ERROR') return false;
        if (lastRunStatusFilter === 'NEVER' && job.last_run_at) return false;
      }

      return true;
    });
  }, [jobs, searchTerm, statusFilter, scheduleTypeFilter, ownerFilter, lastRunStatusFilter]);

  // Pagination
  const totalFiltered = filteredJobs.length;
  const totalPages = Math.ceil(totalFiltered / pageSize) || 1;
  const paginatedJobs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, scheduleTypeFilter, ownerFilter, lastRunStatusFilter, pageSize]);

  const isMechanismEnabled =
    capability?.status === 'supported_enabled' ||
    (capability?.status || '').toLowerCase().includes('enabled');

  return (
    <div className="space-y-4 pb-12 font-sans relative">
      {/* Sub-Tab Navigation Switcher */}
      <div className="flex items-center gap-1.5 p-1 bg-gray-100 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700/80 w-fit">
        <button
          onClick={() => setActiveSubTab('jobs')}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeSubTab === 'jobs'
              ? 'bg-white dark:bg-gray-900 text-orange-600 dark:text-orange-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
        >
          <Activity className="w-4 h-4" />
          <span>Scheduled Jobs</span>
          {jobs.length > 0 && (
            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-400 font-mono">
              {jobs.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveSubTab('backups')}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeSubTab === 'backups'
              ? 'bg-white dark:bg-gray-900 text-orange-600 dark:text-orange-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
        >
          <Archive className="w-4 h-4" />
          <span>Backups & Recovery</span>
        </button>
      </div>

      {activeSubTab === 'backups' ? (
        <OracleBackupView
          activeCluster={activeCluster}
          clientId={clientId}
          refreshKey={refreshKey}
          dbType={dbType}
        />
      ) : (
        <>
          {/* Top Banner Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-900/90 border border-gray-200 dark:border-gray-800/80 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 dark:bg-orange-600/20 border border-orange-300 dark:border-orange-500/30 flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                    Oracle Schedulers & Jobs
                  </h1>
                  {capability?.mechanism &&
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                      {capability?.mechanism?.toUpperCase()}
                    </span>
                  }
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1 ${isMechanismEnabled
                        ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
                        : 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                      }`}
                  >
                    {isMechanismEnabled && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                    {isMechanismEnabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Automated database maintenance tasks, calendar schedules, and background worker jobs
                </p>
              </div>
            </div>

            {/* Action Toolbar */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchJobs(true)}
                disabled={loading || refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {/* Total Jobs */}
            <button
              onClick={() => {
                setStatusFilter('ALL');
                setLastRunStatusFilter('ALL');
              }}
              className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer shadow-sm ${statusFilter === 'ALL' && lastRunStatusFilter === 'ALL'
                  ? 'bg-orange-50/70 dark:bg-orange-950/20 border-orange-500/50 shadow-orange-500/10 ring-2 ring-orange-500/20'
                  : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Total Jobs
                </span>
                <FileText className="w-4 h-4 text-orange-500" />
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white font-mono">
                {totalCount.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Discovered scheduler tasks</div>
            </button>

            {/* Enabled */}
            <button
              onClick={() => setStatusFilter(statusFilter === 'ENABLED' ? 'ALL' : 'ENABLED')}
              className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer shadow-sm ${statusFilter === 'ENABLED'
                  ? 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-500/50 shadow-emerald-500/10 ring-2 ring-emerald-500/20'
                  : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  Active / Enabled
                </span>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                {enabledCount.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Ready to execute on schedule</div>
            </button>

            {/* Disabled */}
            <button
              onClick={() => setStatusFilter(statusFilter === 'DISABLED' ? 'ALL' : 'DISABLED')}
              className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer shadow-sm ${statusFilter === 'DISABLED'
                  ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-500/50 shadow-amber-500/10 ring-2 ring-amber-500/20'
                  : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                  Disabled
                </span>
                <XCircle className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-xl font-bold text-amber-600 dark:text-amber-400 font-mono">
                {disabledCount.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Paused or inactive jobs</div>
            </button>

            {/* Succeeded Last Run */}
            <button
              onClick={() => setLastRunStatusFilter(lastRunStatusFilter === 'SUCCEEDED' ? 'ALL' : 'SUCCEEDED')}
              className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer shadow-sm ${lastRunStatusFilter === 'SUCCEEDED'
                  ? 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-500/50 shadow-blue-500/10 ring-2 ring-blue-500/20'
                  : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  Last Succeeded
                </span>
                <PlayCircle className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400 font-mono">
                {succeededCount.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Completed successfully</div>
            </button>

            {/* Scheduled Upcoming */}
            <div className="p-3.5 rounded-xl border bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 shadow-sm col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                  Upcoming Runs
                </span>
                <Timer className="w-4 h-4 text-purple-500" />
              </div>
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400 font-mono">
                {hasUpcomingNextRun.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">With future next run set</div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 p-3.5 shadow-sm space-y-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search jobs by name, owner, schedule frequency..."
                  className="w-full pl-9 pr-8 py-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700/80 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Status Filter */}
                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px]">State:</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
                  >
                    <option value="ALL" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">All States</option>
                    <option value="ENABLED" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Enabled</option>
                    <option value="DISABLED" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Disabled</option>
                  </select>
                </div>

                {/* Schedule Type Filter */}
                {scheduleTypesList.length > 0 && (
                  <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                    <span className="text-gray-500 dark:text-gray-400 text-[11px]">Type:</span>
                    <select
                      value={scheduleTypeFilter}
                      onChange={(e) => setScheduleTypeFilter(e.target.value)}
                      className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
                    >
                      <option value="ALL" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">All Schedule Types</option>
                      {scheduleTypesList.map((t) => (
                        <option key={t} value={t} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Owner Filter */}
                {ownersList.length > 1 && (
                  <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                    <span className="text-gray-500 dark:text-gray-400 text-[11px]">Owner:</span>
                    <select
                      value={ownerFilter}
                      onChange={(e) => setOwnerFilter(e.target.value)}
                      className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
                    >
                      <option value="ALL" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">All Owners</option>
                      {ownersList.map((o) => (
                        <option key={o} value={o} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Page Size */}
                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px]">Rows:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
                  >
                    <option value={15} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">15</option>
                    <option value={25} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">25</option>
                    <option value={50} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">50</option>
                    <option value={100} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">100</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Active Filter Chips */}
            {(statusFilter !== 'ALL' || scheduleTypeFilter !== 'ALL' || ownerFilter !== 'ALL' || lastRunStatusFilter !== 'ALL' || searchTerm) && (
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-[11px]">
                  <Filter className="w-3 h-3 text-orange-500" />
                  <span>Active filters applied. Showing {filteredJobs.length} of {jobs.length} jobs.</span>
                </div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('ALL');
                    setScheduleTypeFilter('ALL');
                    setOwnerFilter('ALL');
                    setLastRunStatusFilter('ALL');
                  }}
                  className="text-[11px] text-orange-600 dark:text-orange-400 hover:underline cursor-pointer font-medium"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Jobs Table */}
          <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 shadow-sm overflow-hidden">
            {loading ? (
              <div className="py-20 flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                <p className="text-xs text-gray-500 dark:text-gray-400">Loading Oracle DBMS Scheduler jobs...</p>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="py-16 text-center">
                <Calendar className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">No scheduled jobs found</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Try adjusting your search query or status filters.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700/80 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <th className="py-2.5 px-3.5">Job Name</th>
                      <th className="py-2.5 px-3 w-24">State</th>
                      <th className="py-2.5 px-3 w-28">Type</th>
                      <th className="py-2.5 px-3">Schedule / Interval</th>
                      <th className="py-2.5 px-3 w-48">Last Run</th>
                      <th className="py-2.5 px-3 w-44">Next Run</th>
                      <th className="py-2.5 px-3 w-20 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 font-sans text-xs">
                    {paginatedJobs.map((job) => {
                      const isCopied = copiedName === job.name;
                      const hasLastRun = !!job.last_run_at;
                      const isSucceeded = (job.last_run_status || '').toUpperCase() === 'SUCCEEDED';
                      const isFailed = (job.last_run_status || '').toUpperCase() === 'FAILED';

                      return (
                        <tr
                          key={job.name}
                          className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors"
                        >
                          {/* Name & Owner */}
                          <td className="py-2.5 px-3.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono font-bold text-gray-900 dark:text-white text-xs">
                                {job.name}
                              </span>
                              <button
                                onClick={() => handleCopy(job.name)}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer"
                                title="Copy job name"
                              >
                                {isCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                            {job.owner && (
                              <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5 font-mono">
                                <span>Owner: {job.owner}</span>
                              </div>
                            )}
                          </td>

                          {/* State */}
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            {job.enabled ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                ENABLED
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                DISABLED
                              </span>
                            )}
                          </td>

                          {/* Schedule Type */}
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                              {job.schedule_type || 'STANDARD'}
                            </span>
                          </td>

                          {/* Schedule Expression */}
                          <td className="py-2.5 px-3 text-xs">
                            {job.schedule ? (
                              <div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-200 font-mono text-[11px]">
                                <Clock className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                                <span className="truncate max-w-md">{job.schedule}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs italic">Managed by {job.schedule_type || 'Scheduler'}</span>
                            )}
                          </td>

                          {/* Last Run */}
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            {hasLastRun ? (
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1.5">
                                  {isSucceeded && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                      SUCCEEDED
                                    </span>
                                  )}
                                  {isFailed && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
                                      FAILED
                                    </span>
                                  )}
                                  {job.last_run_duration_sec !== undefined && (
                                    <span className="text-[10px] font-mono text-gray-400">
                                      {job.last_run_duration_sec}s
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-gray-700 dark:text-gray-300">
                                  {formatDate(job.last_run_at)}
                                </div>
                                <div className="text-[10px] text-gray-400">
                                  {formatRelativeTime(job.last_run_at)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-[11px] italic">Never executed</span>
                            )}
                          </td>

                          {/* Next Run */}
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            {job.next_run_at ? (
                              <div>
                                <div className="text-[11px] font-semibold text-gray-900 dark:text-white">
                                  {formatDate(job.next_run_at)}
                                </div>
                                <div className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">
                                  {formatRelativeTime(job.next_run_at)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-[11px] italic">Not scheduled</span>
                            )}
                          </td>

                          {/* Action */}
                          <td className="py-2.5 px-3 whitespace-nowrap text-right">
                            <button
                              onClick={() => setSelectedJob(job)}
                              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-orange-600 dark:text-orange-400 transition-colors cursor-pointer"
                              title="View job details"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Bar */}
            {!loading && filteredJobs.length > 0 && (
              <div className="py-3 px-4 bg-gray-50/80 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600 dark:text-gray-400">
                <div>
                  Showing <span className="font-semibold text-gray-900 dark:text-white">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {Math.min(currentPage * pageSize, totalFiltered)}
                  </span>{' '}
                  of <span className="font-semibold text-gray-900 dark:text-white">{totalFiltered.toLocaleString()}</span> jobs
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 text-xs font-medium text-gray-800 dark:text-gray-200">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Job Details Drawer Modal */}
          {selectedJob &&
            createPortal(
              <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="w-full max-w-lg bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-800 overflow-hidden">
                  {/* Drawer Header */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/40">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 rounded-lg bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400">
                        <Calendar className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                          {selectedJob.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Owner: {selectedJob.owner || 'SYS'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedJob(null)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Drawer Content */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Status Badges */}
                    <div className="flex items-center gap-2">
                      {selectedJob.enabled ? (
                        <span className="px-2.5 py-0.5 rounded text-xs font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                          ENABLED
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded text-xs font-bold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                          DISABLED
                        </span>
                      )}
                      <span className="px-2.5 py-0.5 rounded text-xs font-mono font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                        {selectedJob.schedule_type || 'STANDARD'}
                      </span>
                    </div>

                    {/* Schedule Expression Card */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Schedule Expression
                      </label>
                      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 text-xs font-mono text-gray-800 dark:text-gray-200">
                        {selectedJob.schedule || `Type: ${selectedJob.schedule_type || 'NAMED'}`}
                      </div>
                    </div>

                    {/* Execution Details Table */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Execution Status
                      </label>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            Last Run Status
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {selectedJob.last_run_status || 'Never Executed'}
                          </span>
                        </div>

                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            Last Run Duration
                          </span>
                          <span className="font-mono text-gray-900 dark:text-white">
                            {selectedJob.last_run_duration_sec !== undefined ? `${selectedJob.last_run_duration_sec}s` : '—'}
                          </span>
                        </div>

                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            Last Run At
                          </span>
                          <span className="text-gray-900 dark:text-white">
                            {formatDate(selectedJob.last_run_at)}
                          </span>
                        </div>

                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            Next Run At
                          </span>
                          <span className="text-gray-900 dark:text-white">
                            {formatDate(selectedJob.next_run_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Host & Connection Context */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Host & Cluster Context
                      </label>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            Hostname
                          </span>
                          <span className="text-gray-900 dark:text-white">{selectedJob.hostname || '—'}</span>
                        </div>

                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            IP Address
                          </span>
                          <span className="font-mono text-gray-900 dark:text-white">{selectedJob.ip || '—'}</span>
                        </div>

                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 col-span-2">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                            Config Connection
                          </span>
                          <span className="font-mono text-gray-900 dark:text-white truncate block">
                            {selectedJob.config_id || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Raw JSON */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Raw JSON Data
                        </label>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(selectedJob, null, 2));
                            showSuccess('JSON copied to clipboard');
                          }}
                          className="text-xs text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                      <pre className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 text-[11px] font-mono text-gray-800 dark:text-gray-200 max-h-48 overflow-y-auto">
                        {JSON.stringify(selectedJob, null, 2)}
                      </pre>
                    </div>
                  </div>

                  {/* Drawer Footer */}
                  <div className="p-3.5 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/40 flex justify-end">
                    <button
                      onClick={() => setSelectedJob(null)}
                      className="px-4 py-1.5 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
};

export default OracleScheduledJobsView;
